import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { GripVertical, Headphones, Loader2, Upload } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import * as Api from '../../api';
import { measureAudioDuration } from '../../audioDuration';
import { trackGroups } from '../../constants';
import type { AudioPlayback, SectionStimulus } from '../../types';
import { AudioClipRow, AudioDividerRow } from './AudioClipRows';
import { dropClip, moveClip, normalizeDividers, removeClip, setDivider, splitPreview, type DropTarget } from './trackDividers';

interface Props {
  value: SectionStimulus | null | undefined;
  onChange: (next: SectionStimulus | null) => Promise<void> | void;
  disabled?: boolean;
  /** How many questions the section has, to preview the parts the dividers make. */
  questionCount?: number;
}

const MAX_BYTES = 50 * 1024 * 1024;

/** A press must move this far before it becomes a drag, so a click on the handle stays a click. */
const DRAG_START_DISTANCE_PX = 4;

/**
 * The page scrolls inside its own container. dnd-kit's default scrolls it whenever
 * the pointer is within a fifth of the height of either edge (about 40% of the
 * view), so a clip held over its target slid away and landed somewhere else. Only
 * the very edge scrolls now, and slowly; the mouse wheel works during a drag too.
 */
const DRAG_AUTO_SCROLL = { threshold: { x: 0, y: 0.06 }, acceleration: 4 } as const;

const indexOfId = (id: string | number) => Number(String(id).split(':')[1]);

/**
 * What a dragged clip is over. Over a row, the half the dragged clip's centre
 * sits in decides before or after it; over a line, the line itself.
 */
function dropTargetOf({ active, over }: Pick<DragMoveEvent, 'active' | 'over'>): DropTarget | null {
  if (!over) return null;
  const index = indexOfId(over.id);
  if (String(over.id).startsWith('divider:')) return { kind: 'divider', index };
  const moved = active.rect.current.translated;
  const after = moved ? moved.top + moved.height / 2 > over.rect.top + over.rect.height / 2 : false;
  return { kind: 'clip', index, after };
}

const sameTarget = (a: DropTarget | null, b: DropTarget | null) => JSON.stringify(a) === JSON.stringify(b);

/** Reads a local file's duration before upload, so the runner can show progress. */
async function probeDuration(file: File): Promise<number | undefined> {
  const url = URL.createObjectURL(file);
  try {
    const seconds = await measureAudioDuration(url);
    return seconds === undefined ? undefined : Math.round(seconds);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** IELTS Listening audio: upload parts, order them, and choose single-play or practice. */
export function AudioTracksEditor({ value, onChange, disabled, questionCount = 0 }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<{ key: string; url: string } | null>(null);
  const tracks = value?.tracks ?? [];
  const playback: AudioPlayback = value?.playback ?? 'SINGLE_PLAY';
  const groups = trackGroups(tracks);
  const hasDividers = groups.length > 0 && groups[groups.length - 1] >= 1;
  const ranges = splitPreview(tracks, questionCount);

  const perPart = value?.questionsPerPart;
  const [perPartDraft, setPerPartDraft] = useState(perPart ? String(perPart) : '');
  useEffect(() => setPerPartDraft(perPart ? String(perPart) : ''), [perPart]);

  // Patches keep the other settings; with no audio left the stimulus is cleared.
  const save = (patch: Partial<SectionStimulus>) => {
    const next: SectionStimulus = { playback, tracks, ...(perPart ? { questionsPerPart: perPart } : {}), ...patch };
    if (!next.questionsPerPart) delete next.questionsPerPart;
    return onChange(next.tracks.length ? { ...next, tracks: normalizeDividers(next.tracks) } : null);
  };

  const savePerPart = () => {
    const n = Math.round(Number(perPartDraft));
    const nextValue = perPartDraft.trim() === '' ? undefined : n >= 1 && n <= 40 ? n : perPart;
    setPerPartDraft(nextValue ? String(nextValue) : '');
    if (nextValue !== perPart) save({ questionsPerPart: nextValue });
  };

  const upload = async (file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error(t('exam.audio.too_big'));
      return;
    }
    setProgress(0);
    try {
      const durationSec = await probeDuration(file);
      const { data } = await Api.UploadAudio(file, setProgress);
      await save({
        playback,
        tracks: [...tracks, { audioKey: data.data.key, label: file.name.replace(/\.[^.]+$/, ''), ...(durationSec ? { durationSec } : {}) }],
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('exam.audio.upload_failed'));
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const move = (i: number, dir: -1 | 1) => save({ playback, tracks: moveClip(tracks, i, dir) });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: DRAG_START_DISTANCE_PX } }), useSensor(KeyboardSensor));
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [target, setTarget] = useState<DropTarget | null>(null);
  const endDrag = () => {
    setDragFrom(null);
    setTarget(null);
  };
  const onDragEnd = (event: DragEndEvent) => {
    const dropped = dropTargetOf(event);
    endDrag();
    if (!dropped) return;
    const next = dropClip(tracks, indexOfId(event.active.id), dropped);
    if (next !== tracks) save({ playback, tracks: next });
  };

  const preview = async (key: string) => {
    try {
      const { data } = await Api.SignAudio(key);
      setPreviewUrl({ key, url: data.data.url });
    } catch {
      toast.error(t('exam.audio.preview_failed'));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Headphones className="h-4 w-4" aria-hidden /> {t('exam.audio.title')}
        </p>
        <div role="radiogroup" aria-label={t('exam.audio.playback')} className="flex rounded-md border border-border p-0.5 text-xs">
          {(['SINGLE_PLAY', 'PRACTICE'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={playback === mode}
              disabled={disabled || !tracks.length}
              onClick={() => save({ playback: mode, tracks })}
              className={cn('rounded px-2.5 py-1 font-medium', playback === mode ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground')}
            >
              {mode === 'SINGLE_PLAY' ? t('exam.audio.single_play') : t('exam.audio.practice')}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {playback === 'SINGLE_PLAY' ? t('exam.audio.single_play_help') : t('exam.audio.practice_help')}
      </p>

      {tracks.length > 1 && <p className="text-xs text-muted-foreground">{t('exam.audio.dividers_help')}</p>}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        autoScroll={DRAG_AUTO_SCROLL}
        onDragStart={(event: DragStartEvent) => setDragFrom(indexOfId(event.active.id))}
        onDragMove={(event: DragMoveEvent) => {
          const next = dropTargetOf(event);
          setTarget((previous) => (sameTarget(previous, next) ? previous : next));
        }}
        onDragEnd={onDragEnd}
        onDragCancel={endDrag}
      >
        <ol className="space-y-2">
          {tracks.flatMap((track, i) => [
            <AudioClipRow
              key={track.audioKey}
              index={i}
              clip={track}
              count={tracks.length}
              disabled={disabled}
              indicator={target?.kind === 'clip' && target.index === i ? (target.after ? 'after' : 'before') : null}
              previewUrl={previewUrl?.key === track.audioKey ? previewUrl.url : null}
              onRename={(label) => save({ playback, tracks: tracks.map((x, j) => (j === i ? { ...x, label } : x)) })}
              onPreview={() => preview(track.audioKey)}
              onMove={(dir) => move(i, dir)}
              onRemove={() => save({ playback, tracks: removeClip(tracks, i) })}
            />,
            i < tracks.length - 1 ? (
              <AudioDividerRow
                key={`${track.audioKey}-divider`}
                index={i}
                active={Boolean(track.breakAfter)}
                partNumber={(groups[i + 1] ?? 0) + 1}
                disabled={disabled}
                over={target?.kind === 'divider' && target.index === i}
                onToggle={() => save({ playback, tracks: setDivider(tracks, i, !track.breakAfter) })}
              />
            ) : null,
          ])}
        </ol>
        <DragOverlay>
          {dragFrom !== null && tracks[dragFrom] ? (
            <div className="flex items-center gap-2 rounded-md border border-foreground bg-card px-3 py-2 text-sm font-medium text-foreground shadow-lg">
              <GripVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
              <span className="text-xs font-semibold text-muted-foreground">{dragFrom + 1}</span>
              <span className="truncate">{tracks[dragFrom].label}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {ranges.length > 0 && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs font-medium text-foreground">
          {t('exam.audio.split_preview', { ranges: ranges.join(' · ') })}
        </p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/wav,audio/ogg,audio/webm,.mp3,.m4a,.wav,.ogg,.webm"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
        }}
      />
      <Button type="button" variant="outline" disabled={disabled || progress !== null} onClick={() => inputRef.current?.click()} className="gap-2">
        {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Upload className="h-4 w-4" aria-hidden />}
        {progress !== null ? t('exam.audio.uploading', { percent: progress }) : t('exam.audio.upload')}
      </Button>
      <p className="text-xs text-muted-foreground">{t('exam.audio.formats')}</p>

      <label className="block space-y-1 border-t border-border pt-3 text-sm">
        <span className="font-medium text-foreground">{t('exam.audio.per_part')}</span>
        <Input
          type="number"
          min={1}
          max={40}
          inputMode="numeric"
          placeholder="10"
          value={perPartDraft}
          disabled={disabled || !tracks.length || hasDividers}
          onChange={(e) => setPerPartDraft(e.target.value)}
          onBlur={savePerPart}
          onKeyDown={(e) => e.key === 'Enter' && savePerPart()}
          className="h-9 w-24"
        />
        <span className="block text-xs text-muted-foreground">
          {hasDividers ? t('exam.audio.per_part_dividers') : t('exam.audio.per_part_help')}
        </span>
      </label>
    </div>
  );
}

export default AudioTracksEditor;
